import { generateRandomId } from '@sparklayer-io/spark-dash-core/lib/random';
import { create } from 'zustand';
import { registerResettable } from './reset';

export type NotificationType = 'error' | 'warning' | 'success' | 'notice';

export type Notification = {
  id: string;
  title: string;
  message: string | null;
  type: NotificationType | null;
};

const DEFAULT_TIMEOUT = 8000;

type NotificationsState = {
  notifications: Notification[];
  notify(
    title: string,
    message?: string | null,
    type?: NotificationType | null,
    timeout?: number,
  ): string;
  dismiss(id: string): void;
  reset(): void;
};

export const useNotifications = create<NotificationsState>((set, get) => {
  const timers = new Map<string, ReturnType<typeof setTimeout>>();

  return {
    notifications: [],
    notify(title, message = null, type = null, timeout = DEFAULT_TIMEOUT) {
      const id = generateRandomId();

      set((state) => ({ notifications: [...state.notifications, { id, title, message, type }] }));

      if (timeout) {
        timers.set(
          id,
          setTimeout(() => get().dismiss(id), timeout),
        );
      }

      return id;
    },
    dismiss(id) {
      clearTimeout(timers.get(id));
      timers.delete(id);

      set((state) => ({ notifications: state.notifications.filter((n) => n.id !== id) }));
    },
    reset() {
      for (const timer of timers.values()) {
        clearTimeout(timer);
      }
      timers.clear();

      set({ notifications: [] });
    },
  };
});

registerResettable(useNotifications);
